import qs from 'qs';
import { v4 } from 'uuid';
import { create } from '@most/create';
import { mapRej, map } from '@ux/promise';

// Error
const FETCH_FAILED_ERROR = '@@ux/drivers/fetch failed error';
const NDJSON_START = Symbol('ndjson_start');
const NDJSON_END = Symbol('ndjson_end');
let Method;

(function (Method) {
  Method["GET"] = "GET";
  Method["POST"] = "POST";
  Method["PUT"] = "PUT";
  Method["PATCH"] = "PATCH";
  Method["DELETE"] = "DELETE";
})(Method || (Method = {}));

let Credentials;

(function (Credentials) {
  Credentials["INCLUDE"] = "include";
  Credentials["SAME_ORIGIN"] = "same-origin";
  Credentials["OMIT"] = "omit";
})(Credentials || (Credentials = {}));

const pipe = (...fns) => a => fns.reduce((v, f) => f(v), a);

const doFetch = fetch => ({
  url,
  ...config
}) => fetch(url, config) // fetch will only reject on a network error
.catch(() => {
  // where fetch actualy failed (network error), reject with a unique error
  return Promise.reject({
    error: FETCH_FAILED_ERROR
  });
}).then(resp => {
  // fetch actually resolves when the status is 400/500/etc.
  if (!resp.ok) {
    return resp.text().then(body => Promise.reject({
      status: resp.status,
      statusText: resp.statusText,
      type: resp.type,
      url: resp.url,
      body
    }));
  }

  return Promise.resolve(resp);
});

const createFetch = (fetch, FormData = window.FormData) => {
  return pipe(config => ({
    // default method to GET
    method: Method.GET,
    // credentials default to same origin in modern browsers
    // but older versions required you to explicitly set it
    // apparently the spec changed after every browser had implemented the fetch api!
    credentials: Credentials.SAME_ORIGIN,
    ...config
  }), config => {
    if (!config.data) {
      return config;
    }

    if (config.method === 'GET') {
      const {
        url,
        data,
        ...rest
      } = config;
      const query = qs.stringify(data, {
        addQueryPrefix: true
      });
      return { ...rest,
        url: "".concat(url).concat(query)
      };
    } else if (config.data instanceof FormData) {
      // Do not stringify form data as it will not set the correct headers
      const {
        data,
        ...rest
      } = config;
      return { ...rest,
        body: data
      };
    }

    const {
      data,
      ...rest
    } = config;
    return { ...rest,
      body: JSON.stringify(data)
    };
  }, doFetch(fetch));
};

const http = axios => axios;

const json = () => http => pipe(config => ({ ...config,
  headers: { ...config.headers,
    'Content-Type': 'application/json'
  }
}), http, p => p.then(resp => {
  return resp.json().then(data => {
    return {
      response: resp,
      body: data
    };
  });
}, err => {
  if (err && typeof err.body === 'string') {
    try {
      return Promise.reject({
        response: err,
        body: JSON.parse(err.body)
      });
    } catch (e) {
      return Promise.reject({
        response: err,
        body: err.body
      });
    }
  }

  return Promise.reject(err);
}));

const jsonRpc = () => http => ({
  method,
  data,
  ...rest
}) => {
  const id = v4();
  return http({ ...rest,
    method: Method.POST,
    data: {
      jsonrpc: '2.0',
      id,
      method,
      params: data
    }
  }).then(response => {
    if (response.body.id !== id) {
      return Promise.reject({
        response: response.response,
        body: new Error('Invalid response')
      });
    }

    if (response.body.error) {
      return Promise.reject({
        response: response.response,
        body: response.body.error
      });
    }

    return Promise.resolve({
      response: response.response,
      body: response.body.result
    });
  });
};

const middleware = (defaultConfig = {}) => http => (version, method, params) => {
  /* eslint-disable no-param-reassign */
  if (typeof version === 'string' && typeof method !== 'string') {
    params = method;
    method = version;
    version = null;
  }
  /* eslint-enable */


  const prefixedMethod = version ? "v".concat(version, ".").concat(method) : method;
  const config = {
    url: '/api/app',
    ...defaultConfig,
    method: prefixedMethod,
    data: params
  };
  return http(config);
};

// recusively read the ndjson stream and add each item to the most stream
const readJsonStream = (response, add, end) => {
  // dynamically import as it's an optional import
  return import('can-ndjson-stream') // we need to extract the default export (but not if it's not an es module)
  .then(module => module.default || module).then(can => {
    const stream = can(response.body);
    const reader = stream.getReader();

    const onRead = result => {
      if (result.done) {
        add({
          type: NDJSON_END
        });
        end();
        return;
      }

      add(result.value);
      return reader.read().then(onRead);
    };

    return reader.read().then(onRead);
  });
};

// we still want to handle regular json responses, but output the data
// as if it's a one-line stream
const readJsonAsStream = (response, add, end) => {
  return response.json().then(data => {
    add(data);
    add({
      type: NDJSON_END
    });
    end();
  });
};

// check if the browser supports ReadableStreams
const checkCompatibility = () => {
  try {
    // eslint-disable-next-line no-new
    new ReadableStream(); // eslint-disable-next-line no-new

    new TextDecoder();
    return true;
  } catch (e) {
    return false;
  }
};

const ndjson = (checkCompatibility, readJsonStream, readJsonAsStream) => http => options => {
  return create((add, end, error) => {
    const supported = checkCompatibility();
    Promise.resolve(options) // older browsers (looking at you IE and Edge) don't support ReadableStream as a constructor
    // or TextDecoder, so if we can't do new ReadableStream we're just going to do a normal
    // json request
    .then(config => {
      const accepts = supported ? 'application/x-ndjson, application/json' : 'application/json';
      return { ...config,
        headers: { ...config.headers,
          Accepts: accepts
        }
      };
    }) // pass through to the fetch api
    .then(http).then(response => {
      // emit an event containing the response object
      // lets consumers access headers/statusCode etc.
      add({
        type: NDJSON_START,
        payload: response
      }); // if the response doesn't explicitly say it's returned
      // ndjson, we fall back to reading json

      const contentType = response.headers.get('Content-Type');

      if (contentType === 'application/x-ndjson') {
        return readJsonStream(response, add, end);
      }

      return readJsonAsStream(response, add, end);
    }).catch(err => {
      if (err && typeof err.body === 'string') {
        try {
          return Promise.reject(JSON.parse(err.body));
        } catch (e) {
          return Promise.reject(err.body);
        }
      }

      return Promise.reject(err);
    }).catch(error);
  });
}; // eslint-disable-next-line no-underscore-dangle
var ndjson$1 = (() => ndjson(checkCompatibility, readJsonStream, readJsonAsStream));

const xray = () => http => pipe(config => ({ ...config,
  headers: { ...config.headers,
    'X-Ray-Id': v4()
  }
}), http);

const bodyOnly = () => http => pipe(http, map(resp => resp.body), mapRej(resp => resp.body));

const fetch = createFetch(window.fetch);
const http$1 = fetch;

export { Credentials, FETCH_FAILED_ERROR, Method, NDJSON_END, NDJSON_START, bodyOnly, createFetch, http as createHttp, fetch, http$1 as http, json, jsonRpc, middleware, ndjson$1 as ndjson, pipe, xray };
